import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import lancamentoHorasService from '../../services/lancamentoHoras.service';
import clienteService from '../../services/cliente.service';
import BtnIcone from '../../components/BtnIcone';
import SePode from '../../components/SePode';   // r88
import LancamentoModal from './LancamentoModal';
// r67: rotina de Planejamento de Alocação aberta pelo botão "Planejar"
import PlanejamentoModal from '../Planejamento/PlanejamentoModal';
import BrowseHeader from '../../components/BrowseHeader';                    // r111
import { iconeDaRotina } from '../../constants/iconesRotinas';               // r111

// ════════════════════════════════════════════════════════════════════════════
// LancamentosHorasPage — registro de horas trabalhadas (módulo novo)
// ────────────────────────────────────────────────────────────────────────────
// Sigla das constantes: T=Tipo, M=Movimento, S=Situação, ST=Status
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  azulClaro: '#3b82f6',  // r57: pra dash 85-99%
  borda:   '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:  '#fff',    cinzaBg: '#fafafa',  cinzaSt: '#f3f4f6',
  verde:   '#10b981', verdeBg: '#f0fff4',
  // r206: verde dos botões de exportação pra planilha
  verdePlanilha: '#417D23',
  verdeEscuro: '#047857',  // r57: pra dash >100%
  vermelho:'#dc2626', vermBg:  '#fff0f0',
  amarelo: '#d97706', amlBg:   '#fffbeb',
  roxo:    '#7c3aed', roxoBg:  '#f5f0ff',
  laranja: '#f97316', laranjaBg: '#fff7ed',
  rosa:    '#ec4899',
};

// r57: cor por percentual nas dashs
//   < 50         → vermelho
//   50 a 84      → laranja
//   85 a 99      → azul claro
//   = 100        → azul escuro
//   > 100        → verde escuro
function corPorPct(pct) {
  const p = parseFloat(pct) || 0;
  if (p < 50)   return COR.vermelho;
  if (p <= 84)  return COR.laranja;
  if (p < 100)  return COR.azulClaro;
  if (p === 100) return COR.azul;
  return COR.verdeEscuro;
}

// ─── Domínios ───────────────────────────────────────────────────────────────
const TIPO = {
  P: { label: 'Projeto',                cor: COR.azul     },
  S: { label: 'Sustentação',             cor: COR.roxo     },
  A: { label: 'Atendimento Pontual',     cor: COR.laranja  },
  D: { label: 'Service Desk',            cor: COR.verde    },
};
const MOVIMENTO = {
  L: { label: 'Lançamento',  cor: COR.azul     },
  S: { label: 'Solicitação', cor: COR.roxo     },
  R: { label: 'Retrabalho',  cor: COR.amarelo  },
};
const SITUACAO = {
  P: { label: 'Planejada',  cor: COR.laranja  },
  X: { label: 'Executada',  cor: COR.vermelho },
  // r45: Fechada agora é pílula sólida cinza escuro com letra branca
  F: { label: 'Fechada',    cor: '#4b5563', solido: true },
  // r48: Correção (lançamentos liberados pra alteração mesmo com período fechado)
  C: { label: 'Correção',   cor: COR.amarelo, solido: true },
  // r193: horas apontadas como não faturáveis (valor/hora e total sempre zero)
  N: { label: 'Não Faturável', cor: COR.muted, solido: true },
};
const STATUS = {
  A: { label: 'Aprovado',    cor: COR.verde    },
  R: { label: 'Retrabalho',  cor: COR.laranja  },  // r55: era 'Rejeitado'
  E: { label: 'Em Análise',  cor: COR.amarelo  },
};

// Cor pra tag de cliente quando não tem cor_tag personalizada
const CORES_TAG_PADRAO = [
  COR.verde, COR.azul, COR.roxo, COR.laranja, COR.rosa, COR.amarelo, COR.vermelho,
];

// r52: paleta dedicada de cores pras tags de analista (12 cores)
const CORES_ANALISTA = [
  '#0ea5e9', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899', '#06b6d4',
  '#84cc16', '#f97316', '#6366f1', '#14b8a6', '#a855f7', '#eab308',
];

// Hash determinístico de ID → índice
function corAnalista(id) {
  if (!id) return COR.muted;
  let h = 0;
  const s = String(id);
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return CORES_ANALISTA[h % CORES_ANALISTA.length];
}

// ─── Helpers de formatação ──────────────────────────────────────────────────
function formatarHora(horaSql) {
  if (!horaSql) return '';
  // Postgres TIME vem como "HH:MM:SS" ou às vezes "HH:MM"
  return String(horaSql).substring(0, 5);
}
function formatarData(d) {
  if (!d) return '';
  const data = new Date(d);
  return data.toLocaleDateString('pt-BR');
}
function formatarHoras(h) {
  if (h === null || h === undefined) return '00:00';
  const num = parseFloat(h);
  if (!isFinite(num) || num === 0) return '00:00';
  const horas = Math.floor(num);
  const mins = Math.round((num - horas) * 60);
  return `${String(horas).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}
function formatarMoeda(v) {
  if (v === null || v === undefined) return '0,00';
  return parseFloat(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ─── <Pill> — tag colorida estilo Negócios (clara) ──────────────────────────
// r111: padronizado com TagCliente (12/500/borda na cor)
function Pill({ label, cor, solido }) {
  if (solido) {
    return (
      <span style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: '20px',
        fontSize: '12px', fontWeight: '500',
        backgroundColor: cor,
        color: COR.branco,
        whiteSpace: 'nowrap',
        letterSpacing: '0.3px',
        border: `1px solid ${cor}`,           // r111: borda na própria cor
      }}>{label}</span>
    );
  }
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '20px',
      fontSize: '12px', fontWeight: '500',
      backgroundColor: cor + '22',
      color: cor,
      whiteSpace: 'nowrap',
      border: `1px solid ${cor}`,           // r111: borda na cor da fonte
    }}>{label}</span>
  );
}

// ─── <TagCliente> — pílula sólida (mesmo componente do browse de Negócios) ──
// r56: padronizado com <Pill solido> (fontSize 11px, fontWeight 600)
function TagCliente({ tag, cor }) {
  if (!tag) return <span style={{ color: COR.muted, fontSize: '12px' }}>—</span>;
  const bgCor = cor || '#003399';
  const hex = bgCor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const corTexto = lum > 0.5 ? '#1a1a2e' : '#ffffff';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px',
      borderRadius: '20px',
      fontSize: '11px', fontWeight: '600',
      backgroundColor: bgCor, color: corTexto,
      whiteSpace: 'nowrap',
      letterSpacing: '0.3px',
    }}>{tag}</span>
  );
}

// ─── <MultiSelect> — dropdown com checkboxes + busca interna ────────────────
function MultiSelect({ label, opcoes, valores, onChange, largura, compacto }) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const containerRef = useRef(null);
  const inputBuscaRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setAberto(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Reseta a busca quando abre/fecha; foca no input ao abrir
  useEffect(() => {
    if (aberto) {
      setBusca('');
      setTimeout(() => inputBuscaRef.current?.focus(), 50);
    }
  }, [aberto]);

  const toggleValor = (v) => {
    if (valores.includes(v)) onChange(valores.filter(x => x !== v));
    else onChange([...valores, v]);
  };
  const limpar = (e) => { e.stopPropagation(); onChange([]); };

  // Filtra opções pela busca (case-insensitive, contém)
  const opcoesFiltradas = busca.trim()
    ? opcoes.filter(o => String(o.label || '').toLowerCase().includes(busca.trim().toLowerCase()))
    : opcoes;

  let textoBotao;
  if (valores.length === 0) textoBotao = label;
  else if (valores.length === 1) {
    const op = opcoes.find(o => o.v === valores[0]);
    textoBotao = op?.label || valores[0];
  } else if (valores.length === 2) {
    textoBotao = valores.map(v => opcoes.find(o => o.v === v)?.label || v).join(', ');
  } else {
    textoBotao = `${valores.length} selecionados`;
  }

  const temSelecao = valores.length > 0;
  const h = compacto ? '28px' : '32px';

  return (
    <div ref={containerRef} style={{ position: 'relative', width: largura || '140px' }}>
      <button
        type="button"
        onClick={() => setAberto(a => !a)}
        style={{
          width: '100%', height: h,
          padding: '0 8px',
          paddingRight: temSelecao ? '40px' : '26px',
          border: `1px solid ${temSelecao ? COR.azul : COR.borda}`,
          borderRadius: '4px',
          backgroundColor: temSelecao ? COR.azulBg : COR.branco,
          color: temSelecao ? COR.azul : COR.texto,
          fontSize: '13px', fontWeight: temSelecao ? '600' : '500',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '4px',
          fontFamily: "'Manrope', sans-serif",
          textAlign: 'left',
          overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
          position: 'relative',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{textoBotao}</span>
        {/* Seta SVG (chevron-down) — igual em todos os filtros */}
        <span style={{
          position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
          display: 'flex', alignItems: 'center', pointerEvents: 'none',
        }}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2.5 4l2.5 2.5L7.5 4" stroke="currentColor"
                  strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
        {temSelecao && (
          <span role="button" onClick={limpar}
            style={{
              position: 'absolute', right: '22px', top: '50%', transform: 'translateY(-50%)',
              width: '14px', height: '14px', borderRadius: '50%',
              backgroundColor: COR.azul, color: COR.branco,
              // r57b: X centralizado — fontSize maior + line-height 1; sem padding extra
              fontSize: '14px', fontWeight: '700',
              lineHeight: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }} title="Limpar"
          >×</span>
        )}
      </button>

      {aberto && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 2px)', left: 0,
          minWidth: '100%', maxWidth: '340px',
          backgroundColor: COR.branco,
          border: `1px solid ${COR.borda}`, borderRadius: '5px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 100,
          display: 'flex', flexDirection: 'column',
          maxHeight: '320px',
        }}>
          {/* Campo de busca (filtra opções locamente) */}
          {opcoes.length > 5 && (
            <div style={{
              padding: '6px 8px',
              borderBottom: `1px solid ${COR.borda}`,
              backgroundColor: COR.cinzaBg,
            }}
              onMouseDown={e => e.stopPropagation()}
            >
              <input
                ref={inputBuscaRef}
                type="text" value={busca}
                onChange={e => setBusca(e.target.value)}
                placeholder="Buscar..."
                style={{
                  width: '100%', height: '24px',
                  padding: '0 8px',
                  border: `1px solid ${COR.borda}`, borderRadius: '3px',
                  fontSize: '13px', fontFamily: "'Manrope', sans-serif",
                  outline: 'none',
                }}
              />
            </div>
          )}

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {opcoesFiltradas.length === 0 ? (
              <div style={{ padding: '10px 14px', fontSize: '13px', color: COR.muted, fontStyle: 'italic' }}>
                {busca.trim() ? 'Nada encontrado.' : 'Sem opções.'}
              </div>
            ) : opcoesFiltradas.map(op => {
              const marcado = valores.includes(op.v);
              return (
                <label key={op.v} style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '7px 12px', cursor: 'pointer',
                  fontSize: '13px', color: COR.texto,
                  backgroundColor: marcado ? COR.azulBg : COR.branco,
                  borderBottom: `1px solid ${COR.borda}`,
                }}
                onMouseDown={e => e.stopPropagation()}
                >
                  <input type="checkbox" checked={marcado}
                         onChange={() => toggleValor(op.v)}
                         style={{ cursor: 'pointer' }} />
                  {op.cor && (
                    <span style={{
                      width: '10px', height: '10px', borderRadius: '50%',
                      backgroundColor: op.cor, flexShrink: 0,
                    }} />
                  )}
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {op.label}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── <TooltipDescricao> — ícone que abre a descrição/justificativa ─────────
// r209: era um popover de 320px preso na célula da tabela — apertado pra ler,
// e ainda por cima cortado pelo overflow do card do browse. Virou modal
// centralizado, com largura de leitura e rolagem própria no texto longo.
function TooltipDescricao({ descricao, justificativa }) {
  const [aberto, setAberto] = useState(false);

  // ESC fecha (é só leitura, então clique fora também fecha)
  useEffect(() => {
    if (!aberto) return;
    const handler = (e) => { if (e.key === 'Escape') setAberto(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [aberto]);

  const temConteudo = descricao || justificativa;
  if (!temConteudo) {
    return (
      <span title="Sem descrição" style={{
        display: 'inline-flex', width: '22px', height: '22px',
        alignItems: 'center', justifyContent: 'center',
        color: COR.borda, cursor: 'not-allowed',
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      </span>
    );
  }

  const bloco = (titulo, texto) => (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontSize: '11px', fontWeight: '700', color: COR.muted,
                    textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
        {titulo}
      </div>
      <div style={{ fontSize: '14px', color: COR.texto, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
        {texto}
      </div>
    </div>
  );

  return (
    <>
      <button
        onClick={() => setAberto(true)}
        title="Ver descrição"
        style={{
          width: '22px', height: '22px',
          border: 'none', background: 'transparent',
          color: COR.azul, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          padding: 0,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      </button>

      {aberto && (
        <div onClick={() => setAberto(false)}
             style={{
               position: 'fixed', inset: 0, zIndex: 1200,
               backgroundColor: 'rgba(0,0,0,0.45)',
               display: 'flex', alignItems: 'center', justifyContent: 'center',
               padding: '24px', fontFamily: "'Manrope', sans-serif",
             }}>
          <div onClick={e => e.stopPropagation()}
               style={{
                 backgroundColor: COR.branco, borderRadius: '8px',
                 width: '720px', maxWidth: '100%', maxHeight: '80vh',
                 display: 'flex', flexDirection: 'column',
                 boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
               }}>
            <div style={{
              padding: '12px 18px', backgroundColor: COR.azul, color: COR.branco,
              borderRadius: '8px 8px 0 0', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: '14px', fontWeight: '700',
                             fontFamily: 'Calibri, "Segoe UI", Arial, sans-serif' }}>
                Descrição do lançamento
              </span>
              <button onClick={() => setAberto(false)} title="Fechar (ESC)" style={{
                width: '26px', height: '26px', border: 'none',
                backgroundColor: 'transparent', color: COR.branco,
                fontSize: '20px', cursor: 'pointer', lineHeight: 1,
              }}>×</button>
            </div>

            <div style={{ padding: '18px', overflowY: 'auto', flex: 1 }}>
              {descricao && bloco('Descrição', descricao)}
              {justificativa && bloco('Justificativa', justificativa)}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// r204: Fim = hora_inicio + horas + intervalo. Era calculado inline no render;
// virou helper porque a exportação precisa exatamente do mesmo valor.
function calcularHoraFim(l) {
  if (!l.hora_inicio) return '';
  const [h, m] = l.hora_inicio.split(':').map(Number);
  const totalMin = h * 60 + m + Math.round(parseFloat(l.qtd_horas || 0) * 60)
                              + Math.round(parseFloat(l.intervalo || 0) * 60);
  return `${String(Math.floor(totalMin / 60) % 24).padStart(2, '0')}:${String(totalMin % 60).padStart(2, '0')}`;
}

// ════════════════════════════════════════════════════════════════════════════
// Exportação pra Excel (r204)
// ════════════════════════════════════════════════════════════════════════════
// Mesmo padrão do DOS016: monta HTML e entrega pro Excel, sem lib nova.
// Sintética = as colunas da grid. Analítica = idem + Descrição e Justificativa.
// ⚠ Como é HTML com extensão .xls, o Excel 2016+ avisa "o formato e a extensão
//   não coincidem" ao abrir — é esperado, basta confirmar.

const escaparHtml = (v) => String(v ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

// r205: duração decimal (3.25) → "03:15" pra exibição.
const horasHHMM = (v) => {
  const min = Math.round((parseFloat(v) || 0) * 60);
  const sinal = min < 0 ? '-' : '';
  const abs = Math.abs(min);
  return `${sinal}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
};

// Número pra exibição em pt-BR (1234.5 → "1.234,50"). É só fallback visual:
// no Excel quem manda é o x:num + mso-number-format da célula.
const numeroBR = (v) => (parseFloat(v) || 0)
  .toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// r205: célula numérica. O x:num carrega o valor REAL (invariante de locale) e
// o mso-number-format manda no que aparece. Testado no Excel: sem o x:num o
// valor entra como TEXTO e o formato não pega — a coluna Horas mostrava
// "0.1250000000" em vez de 03:00.
const celulaNumero = (valor, formato, exibicao) =>
  `<td class="c" x:num="${(parseFloat(valor) || 0).toFixed(6)}" style="mso-number-format:'${formato}'">${exibicao}</td>`;

// Os caracteres especiais do mso-number-format vão escapados com barra invertida.
const FMT_HORA  = '[hh]\\:mm';
const FMT_VALOR = '\\#\\,\\#\\#0\\.00';

function colunasExportacao(analitico) {
  // larg   = largura em px. O Excel converte px → caracteres a ~10px/caractere
  // (medido: chars ≈ px/10 − 0,7), então os valores abaixo reproduzem as
  // larguras do modelo rel_lanc_horas.xlsx.
  // alinha = 'l' encosta à esquerda, 'c' centraliza (padrão do modelo).
  const cols = [
    { t: 'Analista',  tipo: 'texto', larg: 109, alinha: 'l', v: l => l.colaborador_nome || l.colaborador_id },
    { t: 'Data',      tipo: 'texto', larg: 115, alinha: 'c', v: l => formatarData(l.data) },
    { t: 'Início',    tipo: 'texto', larg:  66, alinha: 'c', v: l => formatarHora(l.hora_inicio) },
    { t: 'Fim',       tipo: 'texto', larg:  62, alinha: 'c', v: l => calcularHoraFim(l) },
    { t: 'Intervalo', tipo: 'valor', larg:  90, alinha: 'c', v: l => l.intervalo },
    { t: 'Cliente',   tipo: 'texto', larg: 106, alinha: 'c', v: l => l.cliente_tag || l.cliente_fantasia || l.cliente_nome },
    { t: 'Tipo',      tipo: 'texto', larg: 109, alinha: 'c', v: l => (TIPO[l.tipo] || {}).label || '' },
    { t: 'Código',    tipo: 'texto', larg: 100, alinha: 'c', v: l => l.projeto_id || '' },
    { t: 'Projeto',   tipo: 'texto', larg: 400, alinha: 'l', v: l => l.projeto_descricao || '' },
    { t: 'Chamado',   tipo: 'texto', larg: 121, alinha: 'c', v: l => l.chamado || '' },
    { t: 'Horas',     tipo: 'hora',  larg: 114, alinha: 'c', v: l => l.total },
    { t: 'Situação',  tipo: 'texto', larg: 152, alinha: 'c', v: l => (SITUACAO[l.situacao] || {}).label || '' },
    // r205: Vlr/h e Total um pouco mais largas que o modelo — lá os valores
    // eram todos zero; com valor real (1.234,50) o Excel mostraria "###".
    { t: 'Vlr/h',     tipo: 'valor', larg: 117, alinha: 'c', v: l => l.valor_hora },
    { t: 'Total',     tipo: 'valor', larg: 117, alinha: 'c', v: l => l.valor_total },
    { t: 'Status',    tipo: 'texto', larg: 162, alinha: 'c', v: l => (STATUS[l.status] || {}).label || '' },
  ];
  if (analitico) {
    cols.push({ t: 'Descrição',     tipo: 'texto', larg: 475, alinha: 'l', v: l => l.descricao || '' });
    cols.push({ t: 'Justificativa', tipo: 'texto', larg: 307, alinha: 'l', v: l => l.justificativa || '' });
  }
  return cols;
}

function montarHtmlExcel({ linhas, analitico, resumoFiltros }) {
  const cols = colunasExportacao(analitico);
  const totalHoras = linhas.reduce((acc, l) => acc + (parseFloat(l.total) || 0), 0);
  const totalValor = linhas.reduce((acc, l) => acc + (parseFloat(l.valor_total) || 0), 0);
  const agora    = new Date();
  const geradoEm = `${agora.toLocaleDateString('pt-BR')} - ${agora.toLocaleTimeString('pt-BR')}`;

  // mso-number-format:'\@' trava a célula como texto — sem isso o Excel come o
  // zero à esquerda do chamado e tenta ler "10:30" como hora.
  const celula = (c, valor) => {
    if (c.tipo === 'hora')  return celulaNumero((parseFloat(valor) || 0) / 24, FMT_HORA, horasHHMM(valor));
    if (c.tipo === 'valor') return celulaNumero(valor, FMT_VALOR, numeroBR(valor));
    return `<td class="${c.alinha}" style="mso-number-format:'\\@'">${escaparHtml(valor)}</td>`;
  };

  const colgroup = cols.map(c => `<col width="${c.larg}">`).join('');
  const thead    = cols.map(c => `<th>${escaparHtml(c.t)}</th>`).join('');
  const tbody    = linhas.map(l => `<tr>${cols.map(c => celula(c, c.v(l))).join('')}</tr>`).join('');

  // Faixa de totais: "Total" na 1ª coluna, os totais sob Horas e Total.
  const tfoot = '<tr class="tot">' + cols.map((c, i) => {
    if (i === 0)         return '<td class="l">Total</td>';
    if (c.t === 'Horas') return celulaNumero(totalHoras / 24, FMT_HORA, horasHHMM(totalHoras));
    if (c.t === 'Total') return celulaNumero(totalValor, FMT_VALOR, numeroBR(totalValor));
    return '<td></td>';
  }).join('') + '</tr>';

  return `<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8">
<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
<x:Name>Lançamentos</x:Name>
<x:WorksheetOptions><x:FreezePanes/><x:SplitHorizontal>5</x:SplitHorizontal>
<x:TopRowBottomPane>5</x:TopRowBottomPane><x:ActivePane>2</x:ActivePane>
</x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
<style>
  body, table, td, th { font-family: 'Manrope Medium', Manrope, Calibri, Arial, sans-serif; font-size: 9pt; }
  table { border-collapse: collapse; }
  th { background: #003399; color: #ffffff; font-weight: bold; text-align: center;
       vertical-align: middle; padding: 4px 6px;
       font-family: 'Manrope ExtraBold', Manrope, Calibri, sans-serif; }
  td { vertical-align: middle; padding: 3px 6px; border-bottom: 1px solid #bfbfbf; }
  td.c { text-align: center; }
  td.l { text-align: left; }
  tr.tot td { background: #003399; color: #ffffff; font-weight: bold; border-bottom: none;
              font-family: 'Manrope ExtraBold', Manrope, Calibri, sans-serif; }
  /* Cabeçalho do relatório: dentro da tabela (com colspan) pra virar célula
     mesclada de verdade, em vez de <div> solto que o Excel joga só em A1. */
  td.tit { font-size: 14pt; font-weight: bold; border-bottom: none;
           font-family: 'Manrope ExtraBold', Manrope, Calibri, sans-serif; }
  td.sub { font-size: 9pt; border-bottom: none; }
  td.vazia { border-bottom: none; }
</style>
</head>
<body>
<table>
<colgroup>${colgroup}</colgroup>
<tbody>
<tr><td class="tit" colspan="${cols.length}">Lançamento de Horas | ${analitico ? 'Analítica' : 'Sintética'}</td></tr>
<tr><td class="sub" colspan="${cols.length}">Gerado em ${escaparHtml(geradoEm)} · ${linhas.length} registro${linhas.length !== 1 ? 's' : ''}</td></tr>
<tr><td class="sub" colspan="${cols.length}">Filtros: ${escaparHtml(resumoFiltros || 'nenhum')}</td></tr>
<tr><td class="vazia" colspan="${cols.length}">&nbsp;</td></tr>
</tbody>
<thead><tr>${thead}</tr></thead>
<tbody>${tbody}${tfoot}</tbody>
</table>
</body></html>`;
}

function baixarArquivo(conteudo, nomeArquivo) {
  // BOM na frente pra o Excel abrir os acentos certos.
  const blob = new Blob(['\ufeff' + conteudo], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = nomeArquivo;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─── Helpers de inicialização (r49) ─────────────────────────────────────────
const mesAtualMMAAAA = () => {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const STORAGE_KEY = (uid) => `lancamentos-horas.filtros.${uid || 'anon'}`;

// r203: preferências de layout da tela (filtros recolhidos / dashs minimizados).
// Guardadas à parte dos filtros pra não sujar o objeto que alimenta a busca.
const PREFS_KEY = (uid) => `lancamentos-horas.layout.${uid || 'anon'}`;

function lerPrefs(uid) {
  try {
    const obj = JSON.parse(localStorage.getItem(PREFS_KEY(uid)) || '{}');
    return {
      filtrosExpandidos: obj.filtrosExpandidos === true,   // default: recolhido
      dashsAbertos:      obj.dashsAbertos !== false,       // default: visíveis
    };
  } catch (err) {
    return { filtrosExpandidos: false, dashsAbertos: true };
  }
}

const MESES_PT = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

// r203: opções do combo de mês/ano — 18 meses pra trás até 6 pra frente.
// `extra` reinsere um período salvo que caia fora da janela, pra não sumir.
function opcoesPeriodo(extra) {
  const hoje = new Date();
  const out = [];
  for (let i = 18; i >= -6; i--) {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    const v = `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    out.push({ v, label: `${MESES_PT[d.getMonth()]}/${d.getFullYear()}` });
  }
  if (extra && !out.some(o => o.v === extra)) {
    const idx = parseInt(extra.slice(0, 2), 10) - 1;
    out.unshift({ v: extra, label: MESES_PT[idx] ? `${MESES_PT[idx]}/${extra.slice(3)}` : extra });
  }
  return out;
}

// Data de hoje em YYYY-MM-DD (formato dos inputs type="date"), no fuso local
const hojeISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const FILTROS_DEFAULT = {
  analista_ids:    [],
  cliente_ids:     [],
  projeto_ids:     [],
  chamados:        [],
  situacoes:       [],
  statuses:        [],
  tipos:           [],
  movimentos:      [],
  data_de:         '',
  data_ate:        '',
  periodo:         '',
};

// r197: lê os filtros salvos do usuário. Sanitiza contra formato antigo
// (chave que sumiu, array que virou string) — na dúvida cai no default.
function lerFiltrosSalvos(uid) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(uid));
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== 'object') return null;
    const out = { ...FILTROS_DEFAULT };
    Object.keys(FILTROS_DEFAULT).forEach(k => {
      const v = obj[k];
      if (Array.isArray(FILTROS_DEFAULT[k])) {
        if (Array.isArray(v)) out[k] = v;
      } else if (typeof v === 'string') {
        out[k] = v;
      }
    });
    return out;
  } catch (err) {
    return null;
  }
}

// ════════════════════════════════════════════════════════════════════════════
// LancamentosHorasPage
// ════════════════════════════════════════════════════════════════════════════
function LancamentosHorasPage() {
  const { usuario } = useAuth();
  const [lancamentos, setLancamentos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  // r197: filtros voltam como o usuário deixou (localStorage). Só na primeira
  // vez — sem nada salvo — é que valem os defaults do r49: mês atual e, logo
  // depois, o colaborador vinculado ao usuário.
  const filtrosSalvosRef = useRef(lerFiltrosSalvos(usuario?.id));
  const [filtros, setFiltros] = useState(() => filtrosSalvosRef.current || {
    ...FILTROS_DEFAULT,
    periodo: mesAtualMMAAAA(),
  });

  // r49: ordenação padrão = analista, data, hora_inicio (asc)
  const [ordem, setOrdem] = useState({ campo: '', dir: 'asc' });

  // Modal de criação/edição
  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  // r209: lançamento usado como base pra uma cópia (novo, sem data/hora)
  const [copiando, setCopiando] = useState(null);
  const [situacaoInicial, setSituacaoInicial] = useState('X');

  // r67: modal de Planejamento de Alocação (rotina nova)
  const [planejamentoAberto, setPlanejamentoAberto] = useState(false);

  // r76: estado do modal "Concluir Correções"
  const [concluirCorrModal, setConcluirCorrModal] = useState(null);  // null | { lancCorrecao, incluidos, alterados }
  const [concluindoCorr, setConcluindoCorr] = useState(false);

  // ── Inicialização do filtro de analista (r49) ────────────────────────────
  // r197: só roda quando NÃO havia filtro salvo — senão sobrescreveria a
  // seleção que o usuário deixou na última visita.
  const inicializadoRef = useRef(false);
  useEffect(() => {
    if (!usuario?.id || inicializadoRef.current) return;
    inicializadoRef.current = true;
    if (filtrosSalvosRef.current) return;

    // Descobre o colaborador vinculado ao usuário usando a busca já existente
    (async () => {
      try {
        const lista = await lancamentoHorasService.buscarColaboradores('');
        // Procura por nome_amigavel ou nome igual ao usuário
        const nomeUser = (usuario.nomeAmigavel || '').toLowerCase().trim();
        const meu = lista.find(c =>
          (c.nome_amigavel || '').toLowerCase() === nomeUser ||
          (c.nome || '').toLowerCase() === nomeUser
        );
        setFiltros(f => ({
          ...f,
          analista_ids: meu ? [meu.id] : [],
        }));
      } catch (err) {
        console.warn('Não foi possível pré-selecionar o analista:', err.message);
      }
    })();
  }, [usuario]);

  // ── r203: layout da tela (filtros recolhidos / dashs minimizados) ────────
  const prefsRef = useRef(lerPrefs(usuario?.id));
  const [filtrosExpandidos, setFiltrosExpandidos] = useState(prefsRef.current.filtrosExpandidos);
  const [dashsAbertos, setDashsAbertos]           = useState(prefsRef.current.dashsAbertos);

  useEffect(() => {
    if (!usuario?.id) return;
    try {
      localStorage.setItem(PREFS_KEY(usuario.id),
        JSON.stringify({ filtrosExpandidos, dashsAbertos }));
    } catch (err) {}
  }, [filtrosExpandidos, dashsAbertos, usuario]);

  // ── Persiste filtros no localStorage (apenas pra retorno entre filtros) ──
  useEffect(() => {
    if (!usuario?.id) return;
    try {
      localStorage.setItem(STORAGE_KEY(usuario.id), JSON.stringify(filtros));
    } catch (err) {}
  }, [filtros, usuario]);

  // ── Carregar ──────────────────────────────────────────────────────────────
  const carregar = useCallback(async () => {
    if (!usuario) return;
    try {
      setCarregando(true);
      setErro('');
      const params = { usuario_id: usuario.id };
      if (filtros.analista_ids.length)  params.analista_ids = filtros.analista_ids.join(',');
      if (filtros.cliente_ids.length)   params.cliente_ids  = filtros.cliente_ids.join(',');
      if (filtros.projeto_ids.length)   params.projeto_ids  = filtros.projeto_ids.join(',');
      if (filtros.chamados.length)      params.chamados     = filtros.chamados.join(',');
      if (filtros.tipos.length)         params.tipos        = filtros.tipos.join(',');
      if (filtros.movimentos.length)    params.movimentos   = filtros.movimentos.join(',');
      if (filtros.situacoes.length)     params.situacoes    = filtros.situacoes.join(',');
      if (filtros.statuses.length)      params.statuses     = filtros.statuses.join(',');
      if (filtros.data_de)              params.data_de      = filtros.data_de;
      if (filtros.data_ate)             params.data_ate     = filtros.data_ate;
      if (filtros.periodo)              params.periodo      = filtros.periodo;
      // r49: ordenação dinâmica
      if (ordem.campo)                  { params.ordem = ordem.campo; params.dir = ordem.dir; }

      const dados = await lancamentoHorasService.listar(params);
      setLancamentos(dados);
    } catch (err) {
      console.error(err);
      setErro('Erro ao carregar lançamentos.');
    } finally {
      setCarregando(false);
    }
  }, [usuario, filtros, ordem]);

  useEffect(() => {
    const t = setTimeout(() => carregar(), 350);
    return () => clearTimeout(t);
  }, [carregar]);

  // ── Opções dos multi-selects (carregadas UMA VEZ via endpoint dedicado) ──
  // Pra que ao marcar um item o dropdown não perca opções que sumiram do
  // resultado filtrado. Recarrega quando criamos/excluímos lançamento.
  const [opcoesFiltros, setOpcoesFiltros] = useState({
    analistas: [], clientes: [], projetos: [], chamados: [],
  });

  const recarregarOpcoes = useCallback(async () => {
    if (!usuario?.id) return;
    try {
      const opc = await lancamentoHorasService.opcoesFiltros(usuario.id);
      setOpcoesFiltros({
        analistas: opc.analistas || [],
        clientes:  opc.clientes  || [],
        projetos:  opc.projetos  || [],
        chamados:  opc.chamados  || [],
      });
    } catch (err) {
      console.warn('Falha ao carregar opções dos filtros:', err.message);
    }
  }, [usuario]);

  useEffect(() => { recarregarOpcoes(); }, [recarregarOpcoes]);

  const opcoesAnalistas = opcoesFiltros.analistas;
  const opcoesClientes  = opcoesFiltros.clientes;
  const opcoesProjetos  = opcoesFiltros.projetos;
  const opcoesChamados  = opcoesFiltros.chamados;

  // r76: derivações pro botão "Concluir Correções"
  //   - habilitado se filtro tem exatamente 1 analista E há lançamentos 'C' no resultado
  const lancCorrecao = useMemo(
    () => (lancamentos || []).filter(l => l.situacao === 'C'),
    [lancamentos]
  );
  const analistasFiltrados = filtros.analista_ids || [];
  const podeConcluirCorr = analistasFiltrados.length === 1 && lancCorrecao.length > 0;

  // ── Total filtros ativos ─────────────────────────────────────────────────
  const totalFiltros = useMemo(() => {
    let n = 0;
    Object.entries(filtros).forEach(([k, v]) => {
      if (Array.isArray(v) && v.length > 0) n++;
      else if (k === 'data_de' || k === 'data_ate' || k === 'periodo') {
        if (v) n++;
      }
    });
    return n;
  }, [filtros]);

  // r203: filtros ativos que ficam escondidos no bloco recolhido — viram badge
  // no botão "Mais filtros", pra ninguém filtrar sem perceber.
  const filtrosOcultosAtivos = useMemo(() => {
    let n = 0;
    ['cliente_ids', 'projeto_ids', 'chamados', 'situacoes', 'statuses'].forEach(k => {
      if ((filtros[k] || []).length > 0) n++;
    });
    if (filtros.data_de)  n++;
    if (filtros.data_ate) n++;
    return n;
  }, [filtros]);

  // r203: opções do combo mm/aaaa (recalcula só se o período mudar)
  const periodosDisponiveis = useMemo(() => opcoesPeriodo(filtros.periodo), [filtros.periodo]);

  // ── State pra horas previstas (capacidade teórica do período) ─────────────
  // r47 item 5: calculado por endpoint dedicado conforme filtros de data e analista.
  // r57: agora também retorna `valor` (valor previsto = soma horas × vlr_hora por colab/dia)
  const [horasPrevistas, setHorasPrevistas] = useState({ horas: 0, valor: 0, info: '' });

  const recarregarPrevistas = useCallback(async () => {
    if (!usuario?.id) return;
    try {
      const params = { usuario_id: usuario.id };
      if (filtros.periodo)   params.periodo  = filtros.periodo;
      if (filtros.data_de)   params.data_de  = filtros.data_de;
      if (filtros.data_ate)  params.data_ate = filtros.data_ate;
      if (filtros.analista_ids.length > 0)
        params.analista_ids = filtros.analista_ids.join(',');

      const r = await lancamentoHorasService.horasPrevistas(params);
      setHorasPrevistas({
        horas: r.horas || 0,
        valor: r.valor || 0,   // r57
        info: r.info || '',
      });
    } catch (err) {
      console.warn('Falha ao calcular previstas:', err.message);
      setHorasPrevistas({ horas: 0, valor: 0, info: 'Erro no cálculo' });
    }
  }, [usuario, filtros.periodo, filtros.data_de, filtros.data_ate, filtros.analista_ids]);

  useEffect(() => {
    const t = setTimeout(() => recarregarPrevistas(), 350);
    return () => clearTimeout(t);
  }, [recarregarPrevistas]);

  // ── Cálculos pros boxes do rodapé ────────────────────────────────────────
  const dashboards = useMemo(() => {
    // r193: N (Não Faturável) entra na conta de horas; em valor sempre soma 0.
    const horasSit = { X: 0, P: 0, F: 0, N: 0 };     // Executada / Planejada / Fechada / Não Faturável
    const horasStat = { A: 0, R: 0, E: 0 };          // Aprovado / Retrabalho / Em análise (r55)
    const valoresSit = { X: 0, P: 0, F: 0, N: 0 };
    // r57: removido horasMov e valoresMov (não mais usados; receb.comerciais
    // agora usa horasPrevistas.valor calculado no backend)

    lancamentos.forEach(l => {
      const total = parseFloat(l.total || 0);
      const vlrT  = parseFloat(l.valor_total || 0);
      if (l.situacao in horasSit)  { horasSit[l.situacao]  += total; valoresSit[l.situacao]  += vlrT; }
      if (l.status   in horasStat) { horasStat[l.status]   += total; }
    });

    // r47: o denominador da dash "Horas x Situação" agora é a PREVISTA.
    // As % das demais linhas mostram quanto da previsão foi consumido em cada situação.
    const totalPrevista = horasPrevistas.horas || 0;
    const totalGeral = horasSit.X + horasSit.P + horasSit.F + horasSit.N;

    const pct = (v) => totalPrevista > 0 ? (v / totalPrevista) * 100 : 0;
    const pctStat = (v) => {
      const totSt = horasStat.A + horasStat.R + horasStat.E;
      return totSt > 0 ? (v / totSt) * 100 : 0;
    };

    // r57: valor previsto (Previsto na dash Recebimento) = horas × vlr_hora,
    //      vindo do backend; pct dos demais = consumo desse total previsto.
    const valorPrevisto = horasPrevistas.valor || 0;
    const pctReceb = (v) => valorPrevisto > 0 ? (v / valorPrevisto) * 100 : 0;

    return {
      sit: {
        previstas:   { h: totalPrevista,                p: 100 },
        planejada:   { h: horasSit.P,                   p: pct(horasSit.P) },
        executadas:  { h: horasSit.X,                   p: pct(horasSit.X) },
        fechadas:    { h: horasSit.F,                   p: pct(horasSit.F) },
        naoFaturavel:{ h: horasSit.N,                   p: pct(horasSit.N) },
        total:       { h: totalGeral,                   p: pct(totalGeral) },
      },
      stat: {
        // r55: status R foi renomeado de "Rejeitado" → "Retrabalho"
        solicitadas: { h: horasStat.E,  p: pctStat(horasStat.E) },   // status='E' (Em Análise) → "Em Aprovação"
        aprovadas:   { h: horasStat.A,  p: pctStat(horasStat.A) },
        retrabalho:  { h: horasStat.R,  p: pctStat(horasStat.R) },
        total:       { h: horasStat.A + horasStat.R + horasStat.E, p: 100 },
      },
      receb: {
        // r57: "Previsto" agora usa o valor calculado no backend (horas_previstas × vlr_hora)
        comerciais:  { v: valorPrevisto,       p: 100 },
        planejada:   { v: valoresSit.P,        p: pctReceb(valoresSit.P) },
        executadas:  { v: valoresSit.X,        p: pctReceb(valoresSit.X) },
        fechadas:    { v: valoresSit.F,        p: pctReceb(valoresSit.F) },
        total:       { v: valoresSit.X + valoresSit.P + valoresSit.F,
                       p: pctReceb(valoresSit.X + valoresSit.P + valoresSit.F) },
      },
    };
  }, [lancamentos, horasPrevistas]);

  // ── r204: exportação pra Excel ───────────────────────────────────────────
  // Exporta exatamente o que está na tela: `lancamentos` já vem do backend
  // filtrado e ordenado, então não há o que refiltrar aqui.
  const rotuloDe = (opcoes, v) => (opcoes.find(o => String(o.v) === String(v)) || {}).label || v;

  const resumoFiltros = useMemo(() => {
    const partes = [];
    if (filtros.analista_ids.length) partes.push(`Analista: ${filtros.analista_ids.map(v => rotuloDe(opcoesAnalistas, v)).join(', ')}`);
    if (filtros.periodo)             partes.push(`Mês/Ano: ${filtros.periodo}`);
    if (filtros.data_de)             partes.push(`De: ${formatarData(filtros.data_de)}`);
    if (filtros.data_ate)            partes.push(`Até: ${formatarData(filtros.data_ate)}`);
    if (filtros.cliente_ids.length)  partes.push(`Cliente: ${filtros.cliente_ids.map(v => rotuloDe(opcoesClientes, v)).join(', ')}`);
    if (filtros.projeto_ids.length)  partes.push(`Projeto: ${filtros.projeto_ids.join(', ')}`);
    if (filtros.chamados.length)     partes.push(`Chamado: ${filtros.chamados.join(', ')}`);
    if (filtros.situacoes.length)    partes.push(`Situação: ${filtros.situacoes.map(v => (SITUACAO[v] || {}).label || v).join(', ')}`);
    if (filtros.statuses.length)     partes.push(`Status: ${filtros.statuses.map(v => (STATUS[v] || {}).label || v).join(', ')}`);
    return partes.join(' · ');
  }, [filtros, opcoesAnalistas, opcoesClientes]);

  const exportarExcel = (analitico) => {
    if (!lancamentos.length) return;
    const html = montarHtmlExcel({ linhas: lancamentos, analitico, resumoFiltros });
    const hoje = hojeISO();
    baixarArquivo(html, `lancamentos-horas-${analitico ? 'analitico' : 'sintetico'}-${hoje}.xls`);
  };

  // ── Ações ────────────────────────────────────────────────────────────────
  const abrirNovo = (situacao) => {
    setEditando(null);
    setCopiando(null);
    setSituacaoInicial(situacao);
    setModalAberto(true);
  };
  const abrirEdicao = (l) => {
    setEditando(l);
    setCopiando(null);
    setModalAberto(true);
  };
  // r209: abre um lançamento NOVO com os dados deste, menos data e hora.
  const abrirCopia = (l) => {
    setEditando(null);
    setCopiando(l);
    setModalAberto(true);
  };
  const fechar = (recarregar) => {
    setModalAberto(false);
    setEditando(null);
    setCopiando(null);
    if (recarregar) { carregar(); recarregarOpcoes(); }
  };
  const excluir = async (l) => {
    if (!window.confirm(`Excluir lançamento #${l.id}?`)) return;
    try {
      await lancamentoHorasService.excluir(l.id, usuario.id);
      carregar();
      recarregarOpcoes();
    } catch (err) {
      alert('Falha ao excluir: ' + (err.response?.data?.mensagem || err.message));
    }
  };

  // r48: marca lançamento como executado (transição C → X)
  const marcarCorrigido = async (l) => {
    if (!window.confirm(`Marcar lançamento #${l.id} como executado (corrigido)?\n\nEle não poderá mais ser editado.`)) return;
    try {
      await lancamentoHorasService.marcarCorrigido(l.id, usuario.id);
      carregar();
    } catch (err) {
      alert('Falha: ' + (err.response?.data?.mensagem || err.message));
    }
  };

  // r76+r77+r82: abre modal "Concluir Correções"
  //   r82: lista APENAS lançamentos que foram realmente mexidos durante a
  //        correção (com flag true). Lançamentos que viraram 'C' só por
  //        transição automática do período (sem flag) não aparecem na lista,
  //        mas continuam sendo atualizados pra 'X' ao confirmar (backend
  //        atualiza TODOS os 'C' do colaborador, independentemente da lista).
  //
  //   Categorização:
  //     - Incluídos = incluido_em_correcao = true
  //     - Alterados = alterado_em_correcao = true E incluido_em_correcao = false
  //                   (se tem ambas flags, prioriza Incluídos)
  //     - 'C' sem nenhuma flag → não aparece (mas é atualizado ao confirmar)
  const abrirConcluirCorr = () => {
    const incluidos = lancCorrecao.filter(l => l.incluido_em_correcao === true);
    const alterados = lancCorrecao.filter(l =>
      l.alterado_em_correcao === true && l.incluido_em_correcao !== true
    );
    setConcluirCorrModal({ incluidos, alterados });
  };

  const confirmarConcluirCorr = async () => {
    const colaboradorId = analistasFiltrados[0];
    if (!colaboradorId) return;
    try {
      setConcluindoCorr(true);
      const r = await lancamentoHorasService.marcarCorrigidoLote(colaboradorId, usuario.id);
      setConcluirCorrModal(null);
      alert(`${r.qtd || 0} lançamento(s) concluído(s).`);
      carregar();
    } catch (err) {
      alert('Falha: ' + (err.response?.data?.mensagem || err.message));
    } finally {
      setConcluindoCorr(false);
    }
  };

  // r52: combo flutuante pra alterar situação (P ↔ X) — item 8
  // menuSituacao = { lancamentoId, x, y } | null
  const [menuSituacao, setMenuSituacao] = useState(null);

  // r197: o combo troca livremente entre Planejada, Executada e Não Faturável.
  // Só não mexe quando o lançamento está Fechado, em Correção ou já Aprovado.
  const situacaoEditavel = (l) =>
    l.situacao !== 'F' && l.situacao !== 'C' && l.status !== 'A';

  const abrirMenuSituacao = (e, l) => {
    e.stopPropagation();
    if (!situacaoEditavel(l)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuSituacao({
      lancamentoId: l.id,
      situacaoAtual: l.situacao,
      x: rect.left,
      y: rect.bottom + 4,
    });
  };

  const aplicarSituacao = async (novaSituacao) => {
    if (!menuSituacao) return;
    try {
      await lancamentoHorasService.alterarSituacao(
        menuSituacao.lancamentoId, novaSituacao, usuario.id
      );
      setMenuSituacao(null);
      carregar();
    } catch (err) {
      setMenuSituacao(null);
      alert('Falha: ' + (err.response?.data?.mensagem || err.message));
    }
  };

  // Fechar menu ao clicar fora
  useEffect(() => {
    if (!menuSituacao) return;
    const fechar = () => setMenuSituacao(null);
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, [menuSituacao]);

  const atualizarFiltro = (k, v) => setFiltros(f => ({ ...f, [k]: v }));
  const limparFiltros = () => setFiltros(FILTROS_DEFAULT);

  // r197: atalho "Hoje" — data de/até no dia corrente. O período (mm/aaaa)
  // sai junto, senão o backend continuaria limitando ao mês digitado.
  const filtroHojeAtivo = !!filtros.data_de
    && filtros.data_de === hojeISO() && filtros.data_ate === hojeISO();
  const aplicarHoje = () => {
    const hoje = hojeISO();
    setFiltros(f => filtroHojeAtivo
      ? { ...f, data_de: '', data_ate: '' }                  // clicar de novo solta o filtro
      : { ...f, data_de: hoje, data_ate: hoje, periodo: '' });
  };

  // r49: alterna ordenação ao clicar no cabeçalho
  const toggleOrdem = (campo) => {
    setOrdem(o => {
      if (o.campo !== campo) return { campo, dir: 'asc' };
      if (o.dir === 'asc')   return { campo, dir: 'desc' };
      return { campo: '', dir: 'asc' };  // 3º clique: volta pro default
    });
  };

  // r48: conta lançamentos em correção visíveis (gera banner amarelo)
  const correcoesPendentes = useMemo(() => {
    const periodosComCorrecao = new Map();  // key 'mm/aaaa' → count
    lancamentos.forEach(l => {
      if (l.situacao === 'C') {
        const d = new Date(l.data);
        const key = `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
        periodosComCorrecao.set(key, (periodosComCorrecao.get(key) || 0) + 1);
      }
    });
    return Array.from(periodosComCorrecao.entries()).map(([periodo, qtd]) => ({ periodo, qtd }));
  }, [lancamentos]);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "'Manrope', sans-serif" }}>

      {/* r111: BrowseHeader padronizado no topo */}
      <BrowseHeader
        rotina="lancamentos-horas"
        titulo="Lançamento de Horas"
        descricao="Registro diário de horas trabalhadas por colaborador, cliente, projeto e tipo."
      />

    <div style={{
      fontFamily: "'Manrope', sans-serif",
      // r49: flex column ocupando viewport pra dashs ficarem fixos no rodapé.
      // r111: aumentei o subtrair (160) pra acomodar o BrowseHeader acima.
      display: 'flex', flexDirection: 'column',
      height: 'calc(100vh - 160px)',
      gap: '12px',
    }}>
      {/* r48: Banner de correções pendentes */}
      {correcoesPendentes.length > 0 && (
        <div style={{
          padding: '10px 14px',
          backgroundColor: '#fffbeb',
          border: `1px solid ${COR.amarelo}66`, borderRadius: '6px',
          fontSize: '13px', color: COR.texto,
          display: 'flex', alignItems: 'center', gap: '10px',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: '18px' }}>⚠</span>
          <span>
            {correcoesPendentes.map((c, i) => (
              <span key={c.periodo}>
                Período <strong>{c.periodo}</strong> em correção — <strong>{c.qtd}</strong> pendência{c.qtd !== 1 ? 's' : ''}
                {i < correcoesPendentes.length - 1 && ' · '}
              </span>
            ))}
          </span>
        </div>
      )}
      {/* ═══ Card de Filtros (em grid pra preencher horizontal) ═════════════ */}
      <div style={{
        backgroundColor: COR.branco,
        border: `1px solid ${COR.borda}`,
        borderRadius: '8px',
        padding: '14px 16px',
        flexShrink: 0,
      }}>
        <div style={{
          fontSize: '14px', fontWeight: '600', color: COR.texto,
          marginBottom: '10px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          {/* r203: título + botão que expande/recolhe os filtros avançados */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>Filtros</span>
            <button onClick={() => setFiltrosExpandidos(v => !v)}
              title={filtrosExpandidos ? 'Recolher os filtros avançados' : 'Mostrar todos os filtros'}
              style={{
                height: '24px', padding: '0 10px',
                fontSize: '12px', fontWeight: '600',
                border: `1px solid ${COR.borda}`, borderRadius: '4px',
                backgroundColor: COR.branco, color: COR.azul,
                cursor: 'pointer', fontFamily: "'Manrope', sans-serif",
                display: 'flex', alignItems: 'center', gap: '5px',
              }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                   style={{ transform: filtrosExpandidos ? 'rotate(180deg)' : 'none',
                            transition: 'transform .15s' }}>
                <polyline points="6 9 12 15 18 9"/>
              </svg>
              {filtrosExpandidos ? 'Menos filtros' : 'Mais filtros'}
              {!filtrosExpandidos && filtrosOcultosAtivos > 0 && (
                <span style={{
                  backgroundColor: COR.azul, color: COR.branco,
                  padding: '1px 6px', borderRadius: '8px',
                  fontSize: '10px', fontWeight: '700',
                }}>{filtrosOcultosAtivos}</span>
              )}
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* r197: atalho pros lançamentos do dia (clique de novo pra soltar) */}
            <button onClick={aplicarHoje}
              title={filtroHojeAtivo ? 'Remover o filtro de hoje' : 'Mostrar só os lançamentos de hoje'}
              style={{
                height: '26px', padding: '0 12px',
                fontSize: '13px', fontWeight: '700',
                border: 'none', borderRadius: '4px',
                backgroundColor: COR.azul, color: COR.branco,
                // quando ativo, um anel interno claro dá o aspecto "pressionado"
                boxShadow: filtroHojeAtivo ? 'inset 0 0 0 2px rgba(255,255,255,0.5)' : 'none',
                cursor: 'pointer', fontFamily: "'Manrope', sans-serif",
                display: 'flex', alignItems: 'center', gap: '6px',
              }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="12" y1="15" x2="12.01" y2="15"/>
              </svg>
              Hoje
            </button>
            {totalFiltros > 0 && (
              <button onClick={limparFiltros}
                title="Limpar todos os filtros"
                style={{
                  height: '26px', padding: '0 12px',
                  fontSize: '13px', fontWeight: '600',
                  border: `1px solid ${COR.borda}`, borderRadius: '4px',
                  backgroundColor: COR.cinzaSt, color: COR.texto,
                  cursor: 'pointer', fontFamily: "'Manrope', sans-serif",
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                {/* vassoura */}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 11h-1V3c0-1.1-.9-2-2-2h-2c-1.1 0-2 .9-2 2v8H8c-2.76 0-5 2.24-5 5v7h18v-7c0-2.76-2.24-5-5-5zm-5 8H5v-3c0-1.65 1.35-3 3-3h3v6zm2-8h-2V3h2v8zm6 8h-6v-6h3c1.65 0 3 1.35 3 3v3z"/>
                </svg>
                Limpar
              </button>
            )}
          </div>
        </div>

        {/* Grid: expandido usa 4 colunas em desktop / 2 em tablet / 1 em mobile.
            r203: recolhido mostra só Analista + Mês/Ano, em colunas estreitas. */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: filtrosExpandidos
            ? 'repeat(auto-fit, minmax(180px, 1fr))'
            : 'minmax(200px, 320px) minmax(160px, 240px)',
          gap: '10px',
        }}>
          <MultiSelect compacto label="Analista" largura="100%"
            valores={filtros.analista_ids} onChange={v => atualizarFiltro('analista_ids', v)}
            opcoes={opcoesAnalistas} />

          {/* r203: Mês/Ano agora é combobox — sem digitação nem máscara.
              O valor continua no formato MM/AAAA que o backend já resolve. */}
          <select value={filtros.periodo}
                  title="Mês/Ano de referência"
                  onChange={e => atualizarFiltro('periodo', e.target.value)}
                  style={{
                    width: '100%', height: '28px', padding: '0 6px',
                    border: `1px solid ${filtros.periodo ? COR.azul : COR.borda}`,
                    borderRadius: '4px',
                    backgroundColor: filtros.periodo ? COR.azulBg : COR.branco,
                    color: filtros.periodo ? COR.azul : COR.texto,
                    fontSize: '13px', fontWeight: filtros.periodo ? '600' : '500',
                    fontFamily: "'Manrope', sans-serif",
                    outline: 'none', cursor: 'pointer',
                  }}>
            <option value="">Mês/Ano — todos</option>
            {periodosDisponiveis.map(o => (
              <option key={o.v} value={o.v}>{o.label}</option>
            ))}
          </select>

          {/* r203: daqui pra baixo só aparece com os filtros expandidos */}
          {filtrosExpandidos && (<>
          <input type="date" value={filtros.data_de} title="Data inicial"
                 onChange={e => atualizarFiltro('data_de', e.target.value)}
                 style={{
                   width: '100%', height: '28px', padding: '0 6px',
                   border: `1px solid ${COR.borda}`, borderRadius: '4px',
                   fontSize: '13px', fontFamily: "'Manrope', sans-serif",
                 }} />
          <input type="date" value={filtros.data_ate} title="Data final"
                 onChange={e => atualizarFiltro('data_ate', e.target.value)}
                 style={{
                   width: '100%', height: '28px', padding: '0 6px',
                   border: `1px solid ${COR.borda}`, borderRadius: '4px',
                   fontSize: '13px', fontFamily: "'Manrope', sans-serif",
                 }} />

          <MultiSelect compacto label="Cliente" largura="100%"
            valores={filtros.cliente_ids} onChange={v => atualizarFiltro('cliente_ids', v)}
            opcoes={opcoesClientes} />

          <MultiSelect compacto label="Projetos" largura="100%"
            valores={filtros.projeto_ids} onChange={v => atualizarFiltro('projeto_ids', v)}
            opcoes={opcoesProjetos} />

          <MultiSelect compacto label="Chamados" largura="100%"
            valores={filtros.chamados} onChange={v => atualizarFiltro('chamados', v)}
            opcoes={opcoesChamados} />

          <MultiSelect compacto label="Situação" largura="100%"
            valores={filtros.situacoes} onChange={v => atualizarFiltro('situacoes', v)}
            opcoes={Object.entries(SITUACAO).map(([v, s]) => ({ v, label: s.label, cor: s.cor }))} />

          <MultiSelect compacto label="Status" largura="100%"
            valores={filtros.statuses} onChange={v => atualizarFiltro('statuses', v)}
            opcoes={Object.entries(STATUS).map(([v, s]) => ({ v, label: s.label, cor: s.cor }))} />
          </>)}
        </div>
      </div>

      {erro && (
        <div style={{
          padding: '10px 14px', marginBottom: '10px',
          backgroundColor: COR.vermBg, color: COR.vermelho,
          border: `1px solid ${COR.vermelho}`, borderRadius: '6px',
          fontSize: '12px', fontWeight: '600',
        }}>{erro}</div>
      )}

      {/* ═══ Card do Browse (estilo Negócios) ═════════════════════════════ */}
      {/* r49: flex 1 ocupa todo o espaço sobrando; tabela rola internamente */}
      <div style={{
        backgroundColor: COR.branco,
        border: `1px solid ${COR.borda}`, borderRadius: '8px',
        overflow: 'hidden',
        flex: 1, minHeight: 0,
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header do card: título + contador + ações */}
        <div style={{
          padding: '14px 16px', borderBottom: `1px solid ${COR.borda}`,
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: COR.texto, flex: 1 }}>
            Lançamento de Horas
            <span style={{ fontSize: '12px', fontWeight: '400', color: COR.muted, marginLeft: '8px' }}>{/* r49: contador */}
              {lancamentos.length} registro{lancamentos.length !== 1 ? 's' : ''}
            </span>
          </span>
          {/* r76: botão Concluir Correções — habilitado se 1 analista no filtro
              e há lançamentos em situação 'C' no resultado */}
          <SePode rotina="lancamentos-horas" acao="concluir-correcoes">
          <button
            onClick={abrirConcluirCorr}
            disabled={!podeConcluirCorr || concluindoCorr}
            title={podeConcluirCorr
              ? `Concluir ${lancCorrecao.length} correção(ões) do analista`
              : 'Filtre exatamente 1 analista e tenha lançamentos em Correção'}
            style={{
              padding: '7px 14px', fontSize: '12px', fontWeight: '700',
              backgroundColor: podeConcluirCorr ? COR.amarelo : COR.cinzaBg,
              color: podeConcluirCorr ? COR.branco : COR.muted,
              border: podeConcluirCorr ? 'none' : `1px solid ${COR.borda}`,
              borderRadius: '5px',
              cursor: podeConcluirCorr ? 'pointer' : 'not-allowed',
              opacity: podeConcluirCorr ? 1 : 0.6,
              display: 'flex', alignItems: 'center', gap: '6px',
              fontFamily: "'Manrope', sans-serif",
            }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            Concluir Correções
            {podeConcluirCorr && (
              <span style={{
                backgroundColor: 'rgba(255,255,255,0.25)',
                padding: '1px 6px', borderRadius: '8px', fontSize: '10px',
              }}>{lancCorrecao.length}</span>
            )}
          </button>
          </SePode>
          {/* r204: exportação da grid (respeita os filtros aplicados).
              Sintética = sem descrição; Analítica = com Descrição e Justificativa.
              Dois botões em vez de um menu: o card do browse tem overflow hidden,
              que cortaria qualquer dropdown aberto aqui dentro. */}
          {[
            { analitico: false, label: 'Sintética', dica: 'Exportar pro Excel sem a descrição' },
            { analitico: true,  label: 'Analítica', dica: 'Exportar pro Excel com a descrição e a justificativa' },
          ].map(op => (
            <button key={op.label}
              onClick={() => exportarExcel(op.analitico)}
              disabled={!lancamentos.length}
              title={lancamentos.length
                ? `${op.dica} (${lancamentos.length} registro${lancamentos.length !== 1 ? 's' : ''})`
                : 'Nenhum lançamento pra exportar'}
              style={{
                padding: '7px 12px', fontSize: '12px', fontWeight: '700',
                // r206: fundo e borda #417D23, texto e ícone brancos.
                // Desabilitado só perde opacidade — mantém a identidade da cor.
                backgroundColor: COR.verdePlanilha,
                color: COR.branco,
                border: `1px solid ${COR.verdePlanilha}`,
                borderRadius: '5px',
                cursor: lancamentos.length ? 'pointer' : 'not-allowed',
                opacity: lancamentos.length ? 1 : 0.5,
                display: 'flex', alignItems: 'center', gap: '6px',
                fontFamily: "'Manrope', sans-serif",
              }}>
              {/* planilha */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <line x1="3" y1="9" x2="21" y2="9"/>
                <line x1="9" y1="9" x2="9" y2="21"/>
              </svg>
              {op.label}
            </button>
          ))}

          {/* r67: botão Planejar agora abre a rotina de Planejamento de Alocação
              (modal full-screen com criação em lote, em vez de modal de 1 lançamento). */}
          <SePode rotina="lancamentos-horas" acao="planejar">
          <button onClick={() => setPlanejamentoAberto(true)} style={{
            padding: '7px 14px', fontSize: '12px', fontWeight: '700',
            backgroundColor: COR.laranja, color: COR.branco,
            border: 'none', borderRadius: '5px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '6px',
            fontFamily: "'Manrope', sans-serif",
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
            </svg>
            Planejar
          </button>
          </SePode>
          <SePode rotina="lancamentos-horas" acao="incluir">
          <button onClick={() => abrirNovo('X')} style={{
            padding: '7px 14px', fontSize: '12px', fontWeight: '700',
            backgroundColor: COR.azul, color: COR.branco,
            border: 'none', borderRadius: '5px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '6px',
            fontFamily: "'Manrope', sans-serif",
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/>
              <path d="M9 14l2 2 4-4"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            Lançar
          </button>
          </SePode>
        </div>

        <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>{/* r49: rola vertical e horizontal */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ backgroundColor: COR.azul }}>
              {/* r111: 1ª coluna fixa pro ícone SVG da rotina */}
              <th style={{ padding: '10px 14px', width: '36px' }}></th>
              {[
                { label: 'Analista',  campo: 'analista'    },
                { label: 'Data',      campo: 'data'        },
                { label: 'Início',    campo: 'hora_inicio' },
                { label: 'Fim',       campo: null          },
                { label: 'Intervalo', campo: 'intervalo'   },
                { label: 'Cliente',   campo: 'cliente'     },
                { label: 'Tipo',      campo: 'tipo'        },
                { label: 'Código',    campo: 'codigo'      },
                { label: 'Projeto',   campo: 'projeto'     },
                { label: 'Chamado',   campo: 'chamado'     },
                { label: 'Tarefa',    campo: 'tarefa'      },   /* r241 */
                { label: 'Horas',     campo: 'total'       },
                { label: 'Situação',  campo: 'situacao'    },
                { label: 'Vlr/h',     campo: 'valor_hora'  },
                { label: 'Total',     campo: 'valor_total' },
                { label: 'Status',    campo: 'status'      },
                { label: 'Ações',     campo: null          },
              ].map(({ label, campo }) => {
                const ativo = campo === ordem.campo;
                const seta = ativo ? (ordem.dir === 'asc' ? ' ↑' : ' ↓') : '';
                return (
                  <th key={label}
                      onClick={() => campo && toggleOrdem(campo)}
                      style={{
                        padding: '10px 14px', textAlign: 'left',
                        // r111: fundo azul, texto branco; coluna ativa fica em azulBg claro
                        fontSize: '13px', fontWeight: '700',
                        color: ativo ? '#cce0ff' : COR.branco,
                        letterSpacing: 'normal',
                        whiteSpace: 'nowrap',
                        cursor: campo ? 'pointer' : 'default', userSelect: 'none',
                      }}>
                    {label}<span style={{ fontSize: '12px' }}>{seta}</span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {carregando ? (
              <tr><td colSpan={18} style={{ padding: '30px', textAlign: 'center', color: COR.muted }}>
                Carregando...
              </td></tr>
            ) : lancamentos.length === 0 ? (
              <tr><td colSpan={18} style={{ padding: '30px', textAlign: 'center', color: COR.muted, fontStyle: 'italic' }}>
                Nenhum lançamento encontrado.
              </td></tr>
            ) : (
              lancamentos.map((l, i) => {
                const tipo = TIPO[l.tipo] || { label: '?', cor: COR.muted };
                const sit  = SITUACAO[l.situacao] || { label: '?', cor: COR.muted };
                const stat = STATUS[l.status] || { label: '?', cor: COR.muted };

                // Cor da tag de cliente: se tem cor_tag personalizada, usa; senão palette
                let corTag = l.cliente_cor_tag;
                if (!corTag && l.cliente_id) {
                  const h = l.cliente_id.charCodeAt(0) + l.cliente_id.charCodeAt(l.cliente_id.length - 1);
                  corTag = CORES_TAG_PADRAO[h % CORES_TAG_PADRAO.length];
                }

                // Fim = hora_inicio + qtd_horas + intervalo (estimativa pra exibir)
                // r204: mesmo helper que a exportação usa.
                const horaFim = calcularHoraFim(l);

                return (
                  <tr key={l.id} style={{
                    borderBottom: `1px solid ${COR.borda}`,
                    backgroundColor: i % 2 === 0 ? COR.branco : COR.cinzaBg,
                  }}>
                    {/* r111: ícone SVG #003399 da rotina (mesmo do menu lateral) */}
                    <td style={{ padding: '10px 0 10px 14px', verticalAlign: 'middle' }}>
                      {iconeDaRotina('lancamentos-horas', { width: 20, height: 20, color: COR.azul })}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <Pill label={l.colaborador_nome || l.colaborador_id}
                            cor={corAnalista(l.colaborador_id)} />
                    </td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: COR.texto }}>{formatarData(l.data)}</td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: COR.texto }}>{formatarHora(l.hora_inicio)}</td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: COR.texto }}>{horaFim}</td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: COR.texto }}>{formatarHoras(l.intervalo)}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <TagCliente
                        tag={l.cliente_tag || l.cliente_fantasia || l.cliente_nome}
                        cor={corTag}
                      />
                    </td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: COR.texto }}>
                      {tipo.label}
                    </td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: COR.texto }}>
                      {l.projeto_id || <span style={{ color: COR.muted }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 14px', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: COR.texto }}
                        title={l.projeto_descricao || ''}>
                      {l.projeto_descricao || <span style={{ color: COR.muted }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 14px', color: COR.muted, whiteSpace: 'nowrap' }}>
                      {l.chamado || '—'}
                    </td>
                    {/* r241: tarefa que originou o lançamento. Em branco quando
                        o lançamento foi feito direto na tela de horas.
                        r242: mesma fonte e cor da coluna Chamado — as duas são
                        o mesmo tipo de informação (número de referência). */}
                    <td style={{ padding: '10px 14px', color: COR.muted, whiteSpace: 'nowrap' }}
                        title={l.tarefa_id
                          ? `Tarefa #${l.tarefa_id}${l.tarefa_titulo ? ' — ' + l.tarefa_titulo : ''}`
                          : ''}>
                      {l.tarefa_id || '—'}
                    </td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', fontWeight: '600', color: COR.texto }}>
                      {formatarHoras(l.total)}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      {/* r52 item 8: Pill clicável abre combo flutuante.
                          r197: bloqueada se Fechada, em Correção ou Aprovada. */}
                      {!situacaoEditavel(l) ? (
                        <Pill label={sit.label} cor={sit.cor} solido={sit.solido} />
                      ) : (
                        <span onMouseDown={(e) => abrirMenuSituacao(e, l)}
                              style={{ cursor: 'pointer', display: 'inline-block' }}
                              title="Clique pra alterar situação">
                          <Pill label={sit.label} cor={sit.cor} solido={sit.solido} />
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', textAlign: 'right', color: COR.texto }}>
                      {formatarMoeda(l.valor_hora)}
                    </td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', textAlign: 'right', fontWeight: '600', color: COR.texto }}>
                      {formatarMoeda(l.valor_total)}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <Pill label={stat.label} cor={stat.cor} />
                    </td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <TooltipDescricao descricao={l.descricao} justificativa={l.justificativa} />
                        {/* r77: indicadores visuais (não-clicáveis) de rastreio de Correção.
                            Substituem o antigo botão ✓ "marcar corrigido" — agora a
                            confirmação é em lote via "Concluir Correções". */}
                        {l.incluido_em_correcao && (
                          <span title="Incluído enquanto em Correção"
                            style={{
                              width: '20px', height: '20px',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              backgroundColor: '#fef9c3', border: `1px solid ${COR.amarelo}`,
                              color: COR.amarelo, borderRadius: '4px',
                              fontSize: '11px', fontWeight: '800',
                            }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" strokeWidth="3"
                                 strokeLinecap="round" strokeLinejoin="round">
                              <line x1="12" y1="5" x2="12" y2="19"/>
                              <line x1="5" y1="12" x2="19" y2="12"/>
                            </svg>
                          </span>
                        )}
                        {l.alterado_em_correcao && (
                          <span title="Alterado enquanto em Correção"
                            style={{
                              width: '20px', height: '20px',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              backgroundColor: COR.azulBg, border: `1px solid ${COR.azul}`,
                              color: COR.azul, borderRadius: '4px',
                              fontSize: '11px', fontWeight: '800',
                            }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" strokeWidth="2.5"
                                 strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </span>
                        )}
                        {/* r209: copiar — repete o lançamento noutro dia/hora.
                            Cria registro novo, então a permissão é 'incluir'.
                            Vale até pra lançamento fechado: a cópia é outro. */}
                        <SePode rotina="lancamentos-horas" acao="incluir">
                        <button onClick={() => abrirCopia(l)}
                          title="Copiar lançamento (abre um novo sem data e hora)"
                          style={{
                            width: '22px', height: '22px',
                            border: 'none', background: 'transparent',
                            color: COR.azul, cursor: 'pointer',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            padding: 0,
                          }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                               stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="9" y="9" width="13" height="13" rx="2"/>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                          </svg>
                        </button>
                        </SePode>
                        <SePode rotina="lancamentos-horas" acao="alterar">
                        <button onClick={() => abrirEdicao(l)}
                          title={l.situacao === 'F' ? 'Lançamento fechado — não pode ser editado' : 'Editar'}
                          disabled={l.situacao === 'F'}
                          style={{
                            width: '22px', height: '22px',
                            border: 'none', background: 'transparent',
                            color: l.situacao === 'F' ? COR.muted : COR.azul,
                            cursor: l.situacao === 'F' ? 'not-allowed' : 'pointer',
                            opacity: l.situacao === 'F' ? 0.4 : 1,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            padding: 0,
                          }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                               stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 20h9"/>
                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/>
                          </svg>
                        </button>
                        </SePode>
                        <SePode rotina="lancamentos-horas" acao="excluir">
                        <button onClick={() => excluir(l)}
                          title={l.situacao === 'F' ? 'Lançamento fechado — não pode ser excluído' : 'Excluir'}
                          disabled={l.situacao === 'F'}
                          style={{
                            width: '22px', height: '22px',
                            border: 'none', background: 'transparent',
                            color: l.situacao === 'F' ? COR.muted : COR.vermelho,
                            cursor: l.situacao === 'F' ? 'not-allowed' : 'pointer',
                            opacity: l.situacao === 'F' ? 0.4 : 1,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            padding: 0,
                          }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                               stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          </svg>
                        </button>
                        </SePode>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* ═══ Footer: 3 boxes de dashboard ═══════════════════════════════════ */}
      {/* r49: fixos no rodapé, não rolam com a tabela.
          r203: minimizáveis — recolhidos, a grid de lançamentos fica com o espaço. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
        <button onClick={() => setDashsAbertos(v => !v)}
          title={dashsAbertos ? 'Minimizar os resumos e ampliar a grid' : 'Mostrar os resumos'}
          style={{
            height: '24px', padding: '0 10px',
            fontSize: '12px', fontWeight: '600',
            border: `1px solid ${COR.borda}`, borderRadius: '4px',
            backgroundColor: COR.branco, color: COR.azul,
            cursor: 'pointer', fontFamily: "'Manrope', sans-serif",
            display: 'flex', alignItems: 'center', gap: '5px',
          }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
               style={{ transform: dashsAbertos ? 'none' : 'rotate(180deg)',
                        transition: 'transform .15s' }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
          {dashsAbertos ? 'Minimizar resumos' : 'Mostrar resumos'}
        </button>
      </div>

      {dashsAbertos && (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '10px',
        flexShrink: 0,
      }}>
        {/* r57: cores das dashs agora calculadas dinamicamente por percentual (item 2).
            Exceção: "Previstas/Previsto" (referência) só fica colorida quando há valor;
            se h=0, fica cinza pra não dar impressão de "100% atingido". */}
        <BoxDashboard titulo="Horas x Situação" linhas={[
          { label: 'Previstas',  valor: formatarHoras(dashboards.sit.previstas.h),  pct: dashboards.sit.previstas.p,
            corPct: dashboards.sit.previstas.h > 0 ? COR.azul : COR.muted },
          { label: 'Planejadas', valor: formatarHoras(dashboards.sit.planejada.h),  pct: dashboards.sit.planejada.p,  corPct: corPorPct(dashboards.sit.planejada.p) },
          { label: 'Executadas', valor: formatarHoras(dashboards.sit.executadas.h), pct: dashboards.sit.executadas.p, corPct: corPorPct(dashboards.sit.executadas.p) },
          { label: 'Fechadas',   valor: formatarHoras(dashboards.sit.fechadas.h),   pct: dashboards.sit.fechadas.p,   corPct: corPorPct(dashboards.sit.fechadas.p) },
          // r193: horas não faturáveis somam no total de horas, mas nunca em valor
          { label: 'Não Faturáveis', valor: formatarHoras(dashboards.sit.naoFaturavel.h), pct: dashboards.sit.naoFaturavel.p, corPct: corPorPct(dashboards.sit.naoFaturavel.p) },
          { label: 'Total',      valor: formatarHoras(dashboards.sit.total.h),      pct: dashboards.sit.total.p,      corPct: corPorPct(dashboards.sit.total.p), total: true },
        ]} />
        <BoxDashboard titulo="Horas x Status" linhas={[
          { label: 'Em Análise',   valor: formatarHoras(dashboards.stat.solicitadas.h), pct: dashboards.stat.solicitadas.p, corPct: corPorPct(dashboards.stat.solicitadas.p) },
          { label: 'Aprovadas',    valor: formatarHoras(dashboards.stat.aprovadas.h),   pct: dashboards.stat.aprovadas.p,   corPct: corPorPct(dashboards.stat.aprovadas.p) },
          { label: 'Retrabalho',   valor: formatarHoras(dashboards.stat.retrabalho.h),  pct: dashboards.stat.retrabalho.p,  corPct: corPorPct(dashboards.stat.retrabalho.p) },
          { label: 'Total',        valor: formatarHoras(dashboards.stat.total.h),       pct: dashboards.stat.total.p,       corPct: corPorPct(dashboards.stat.total.p), total: true },
        ]} />
        {/* r57: cores das dashs agora calculadas dinamicamente por percentual (item 2)
            Exceção: "Previsto" (referência) usa azul escuro fixo OU cinza se ainda zerado. */}
        <BoxDashboard titulo="Recebimento" linhas={[
          { label: 'Previsto',  valor: formatarMoeda(dashboards.receb.comerciais.v), pct: dashboards.receb.comerciais.p,
            corPct: dashboards.receb.comerciais.v > 0 ? COR.azul : COR.muted },
          { label: 'Planejado', valor: formatarMoeda(dashboards.receb.planejada.v),  pct: dashboards.receb.planejada.p,  corPct: corPorPct(dashboards.receb.planejada.p) },
          { label: 'Executado', valor: formatarMoeda(dashboards.receb.executadas.v), pct: dashboards.receb.executadas.p, corPct: corPorPct(dashboards.receb.executadas.p) },
          { label: 'Fechado',   valor: formatarMoeda(dashboards.receb.fechadas.v),   pct: dashboards.receb.fechadas.p,   corPct: corPorPct(dashboards.receb.fechadas.p) },
          { label: 'Total',     valor: formatarMoeda(dashboards.receb.total.v),      pct: dashboards.receb.total.p,      corPct: corPorPct(dashboards.receb.total.p), total: true },
        ]} />
      </div>
      )}

      {/* Modal de criação/edição */}
      {modalAberto && (
        <LancamentoModal
          lancamento={editando}
          copiaDe={copiando}
          situacaoInicial={situacaoInicial}
          onFechar={fechar}
        />
      )}

      {/* r67: Modal de Planejamento de Alocação.
          Pré-seleciona analista conforme regra:
            - admin com 1 analista no filtro → usa esse
            - admin sem analista filtrado → vazio (escolhe dentro)
            - não-admin → próprio colaborador (PlanejamentoModal cuida disso) */}
      {planejamentoAberto && (
        <PlanejamentoModal
          analistaInicial={(() => {
            const ids = filtros.analista_ids || [];
            if (ids.length !== 1) return null;
            const op = opcoesAnalistas.find(a => a.id === ids[0]);
            return op ? { id: op.id, label: op.nome_amigavel || op.nome } : null;
          })()}
          onFechar={(criouAlgo) => {
            setPlanejamentoAberto(false);
            if (criouAlgo) carregar();  // r67: recarrega o browse só se houve criação
          }}
        />
      )}

      {/* r76: modal de confirma\u00e7\u00e3o de Concluir Corre\u00e7\u00f5es */}
      {concluirCorrModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
          fontFamily: "'Manrope', sans-serif",
        }} onClick={() => !concluindoCorr && setConcluirCorrModal(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            backgroundColor: COR.branco, borderRadius: '8px',
            width: '720px', maxWidth: '92%', maxHeight: '85vh',
            display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${COR.borda}`,
                          fontSize: '15px', fontWeight: '700', color: COR.amarelo }}>
              ✓ Concluir Correções — {opcoesAnalistas.find(a => a.id === analistasFiltrados[0])?.nome_amigavel || analistasFiltrados[0]}
            </div>
            <div style={{ padding: '14px 18px', overflowY: 'auto', flex: 1, fontSize: '13px' }}>
              {(() => {
                const totalListado = concluirCorrModal.incluidos.length + concluirCorrModal.alterados.length;
                const totalTransicaoAuto = lancCorrecao.length - totalListado;
                return (
                  <p style={{ marginTop: 0, color: COR.texto }}>
                    Os <strong>{lancCorrecao.length}</strong> lançamento(s) em situação Correção do analista
                    serão marcados como <strong>Executados</strong>.
                    {totalListado > 0 && (
                      <> Destes, <strong>{totalListado}</strong> foram incluídos ou alterados durante
                      a correção (listados abaixo).</>
                    )}
                    {totalTransicaoAuto > 0 && (
                      <> Os outros <strong>{totalTransicaoAuto}</strong> entraram em Correção apenas pela
                      transição automática do período e não foram mexidos.</>
                    )}
                    {' '}Isso encerra a janela de correções deste analista (se for o último).
                  </p>
                );
              })()}

              {/* Incluídos */}
              {concluirCorrModal.incluidos.length > 0 && (
                <div style={{
                  backgroundColor: '#fffbeb', border: `1px solid ${COR.amarelo}`,
                  borderRadius: '6px', padding: '10px 12px', marginTop: '10px',
                }}>
                  <div style={{ fontWeight: '700', color: COR.amarelo, marginBottom: '6px' }}>
                    Incluídos durante a correção ({concluirCorrModal.incluidos.length})
                    <span style={{ fontWeight: '400', color: COR.muted, fontSize: '11px', marginLeft: '6px' }}>
                      criados agora, ainda em "Em Análise"
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: COR.texto }}>
                    {concluirCorrModal.incluidos.slice(0, 50).map(l => (
                      <div key={l.id} style={{
                        padding: '4px 0', borderBottom: `1px dashed ${COR.borda}`,
                        display: 'flex', gap: '10px',
                      }}>
                        <span style={{ minWidth: '90px', fontWeight: '600' }}>
                          {formatarData(l.data)}
                        </span>
                        <span style={{ minWidth: '70px' }}>
                          {String(l.hora_inicio || '').slice(0, 5)} · {parseFloat(l.qtd_horas || 0).toFixed(1)}h
                        </span>
                        <span style={{ flex: 1, color: COR.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {l.projeto_id}{l.projeto_descricao ? ` — ${l.projeto_descricao}` : ''}
                        </span>
                      </div>
                    ))}
                    {concluirCorrModal.incluidos.length > 50 && (
                      <div style={{ paddingTop: '4px', color: COR.muted, fontStyle: 'italic' }}>
                        ... e mais {concluirCorrModal.incluidos.length - 50} lançamentos
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Alterados */}
              {concluirCorrModal.alterados.length > 0 && (
                <div style={{
                  backgroundColor: COR.azulBg, border: `1px solid ${COR.azul}`,
                  borderRadius: '6px', padding: '10px 12px', marginTop: '10px',
                }}>
                  <div style={{ fontWeight: '700', color: COR.azul, marginBottom: '6px' }}>
                    Alterados durante a correção ({concluirCorrModal.alterados.length})
                    <span style={{ fontWeight: '400', color: COR.muted, fontSize: '11px', marginLeft: '6px' }}>
                      já existiam antes
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: COR.texto }}>
                    {concluirCorrModal.alterados.slice(0, 50).map(l => (
                      <div key={l.id} style={{
                        padding: '4px 0', borderBottom: `1px dashed ${COR.borda}`,
                        display: 'flex', gap: '10px',
                      }}>
                        <span style={{ minWidth: '90px', fontWeight: '600' }}>
                          {formatarData(l.data)}
                        </span>
                        <span style={{ minWidth: '70px' }}>
                          {String(l.hora_inicio || '').slice(0, 5)} · {parseFloat(l.qtd_horas || 0).toFixed(1)}h
                        </span>
                        <span style={{ flex: 1, color: COR.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {l.projeto_id}{l.projeto_descricao ? ` — ${l.projeto_descricao}` : ''}
                        </span>
                      </div>
                    ))}
                    {concluirCorrModal.alterados.length > 50 && (
                      <div style={{ paddingTop: '4px', color: COR.muted, fontStyle: 'italic' }}>
                        ... e mais {concluirCorrModal.alterados.length - 50} lançamentos
                      </div>
                    )}
                  </div>
                </div>
              )}

              <p style={{ marginTop: '14px', fontSize: '11px', color: COR.muted, fontStyle: 'italic' }}>
                Lançamentos excluídos durante a correção não são mostrados (não há rastro automático).
                Ao confirmar, <strong>todos</strong> os lançamentos em Correção do analista
                (mexidos ou não) passam para Executado.
              </p>
            </div>
            <div style={{ padding: '12px 18px', borderTop: `1px solid ${COR.borda}`,
                          display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setConcluirCorrModal(null)}
                disabled={concluindoCorr}
                style={{ padding: '8px 16px', fontSize: '12px', fontWeight: '600',
                         backgroundColor: COR.branco, color: COR.muted,
                         border: `1px solid ${COR.borda}`, borderRadius: '4px',
                         cursor: concluindoCorr ? 'not-allowed' : 'pointer' }}>
                Cancelar
              </button>
              <button onClick={confirmarConcluirCorr} disabled={concluindoCorr}
                style={{ padding: '8px 16px', fontSize: '12px', fontWeight: '600',
                         backgroundColor: COR.amarelo, color: COR.branco,
                         border: 'none', borderRadius: '4px',
                         cursor: concluindoCorr ? 'wait' : 'pointer',
                         opacity: concluindoCorr ? 0.6 : 1 }}>
                {concluindoCorr ? 'Processando...' : `Concluir ${lancCorrecao.length} lançamento(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* r52 item 8: Combo flutuante de situação (P ↔ X) */}
      {menuSituacao && (
        <div
          onMouseDown={e => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: menuSituacao.y, left: menuSituacao.x,
            backgroundColor: COR.branco,
            border: `1px solid ${COR.borda}`, borderRadius: '6px',
            boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
            zIndex: 1000, padding: '4px', minWidth: '160px',
            fontFamily: "'Manrope', sans-serif",
          }}>
          {[
            { v: 'P', label: 'Planejada',      cor: COR.laranja },
            { v: 'X', label: 'Executada',      cor: COR.vermelho },
            // r197: não faturável zera valor/hora e valor total do lançamento
            { v: 'N', label: 'Não Faturável',  cor: COR.muted },
          ].map(op => {
            const ativo = op.v === menuSituacao.situacaoAtual;
            return (
              <div key={op.v}
                onClick={() => !ativo && aplicarSituacao(op.v)}
                style={{
                  padding: '8px 12px', borderRadius: '4px',
                  fontSize: '12px', fontWeight: '600',
                  cursor: ativo ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '8px',
                  color: ativo ? COR.muted : COR.texto,
                  backgroundColor: ativo ? COR.cinzaBg : 'transparent',
                }}
                onMouseEnter={e => !ativo && (e.currentTarget.style.backgroundColor = COR.azulBg)}
                onMouseLeave={e => !ativo && (e.currentTarget.style.backgroundColor = 'transparent')}>
                <span style={{
                  width: '10px', height: '10px', borderRadius: '50%',
                  backgroundColor: op.cor, flexShrink: 0,
                }} />
                <span style={{ flex: 1 }}>{op.label}</span>
                {ativo && <span style={{ fontSize: '10px', color: COR.muted }}>atual</span>}
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}

// ─── <BoxDashboard> ─────────────────────────────────────────────────────────
function BoxDashboard({ titulo, linhas }) {
  return (
    <div style={{
      backgroundColor: COR.branco,
      border: `1px solid ${COR.borda}`,
      borderRadius: '6px',
      overflow: 'hidden',  // r55: pra o cantos da faixa cinza não vazarem
    }}>
      {/* r56: título com fonte maior (16px) e cor #404040 */}
      <div style={{
        fontSize: '16px', fontWeight: '700', color: '#404040',
        padding: '10px 18px',
        backgroundColor: COR.cinzaBg,
        borderBottom: `1px solid ${COR.borda}`,
        fontFamily: 'Calibri, "Segoe UI", Arial, sans-serif',
        letterSpacing: '0.3px',
      }}>
        {titulo}
      </div>
      <div style={{ padding: '12px 18px' }}>
        <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
          <tbody>
            {linhas.map((l, i) => (
              <tr key={i} style={{
                borderTop: l.total ? `1px solid ${COR.borda}` : 'none',
                fontWeight: l.total ? '700' : '500',
              }}>
                <td style={{ padding: '5px 0', color: COR.texto }}>{l.label}</td>
                <td style={{ padding: '5px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {l.valor}
              </td>
              <td style={{ padding: '5px 0 5px 12px', textAlign: 'right',
                           color: l.corPct, fontVariantNumeric: 'tabular-nums' }}>
                {l.pct.toFixed(2).replace('.', ',')}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

export default LancamentosHorasPage;
